/**
 * The reporter: the pure rules of `report.ts` joined to a sender, a clock and
 * the page. `reportError` is what the window listeners and the error boundary
 * call; it never throws.
 *
 * Kept free of the app's modules (the `ApiClient`, Capacitor) so the whole
 * path — build, gate, serialize, send — runs in a Node test with fakes.
 * `install.ts` supplies the real ports.
 */

import { buildClientErrorBody } from "@repo/client/data/client-errors";
import type { ClientErrorSource } from "@repo/contract";
import {
  createReportGate,
  isSkippedViewTransition,
  type ReportGate,
  toClientError,
} from "./report";
import type { SendMode, SendReport } from "./send";

export interface ReporterPorts {
  readonly bundleVersion: string;
  readonly gate?: ReportGate;
  /** Whether the page is going away: pagehide seen, or the document hidden. */
  readonly mode: () => SendMode;
  readonly now: () => Date;
  readonly platform: () => string;
  /** `location.search`. */
  readonly route: () => string | undefined;
  readonly send: SendReport;
}

export type ReportError = (source: ClientErrorSource, error: unknown) => void;

export function createReporter(ports: ReporterPorts): ReportError {
  const gate = ports.gate ?? createReportGate();
  return (source, error) => {
    // Before the gate, so it cannot spend one of the session's reports.
    if (isSkippedViewTransition(error)) {
      return;
    }
    try {
      const now = ports.now();
      const report = toClientError(source, error, {
        bundleVersion: ports.bundleVersion,
        now,
        platform: ports.platform(),
        route: ports.route(),
      });
      if (!gate.admit(report, now)) {
        return;
      }
      ports.send(buildClientErrorBody(report), ports.mode());
    } catch {
      // A reporter that throws from inside an `error` listener reports itself
      // forever. Losing the report is the lesser failure.
    }
  };
}
